import { FlexRow } from '@/components/common/Flex';
import { FlexProps } from '@chakra-ui/react';
import { PlentyGoalAccountFinancialEntity } from '@common/financialEntities';
import {
    InvestmentInfoRowProps,
    useInvestmentInfoRowsProps,
} from '@common/react/goals';
import styleguide from '@common/styleguide';
import { EntityViewGridSection } from '../common/EntityView';
import { FlexCol } from '../common/Flex';
import FormulaRow from '../common/FormulaRow';
import Icon from '../common/icons/Icon';
import { InfoButtonWithModal } from '../common/InfoModal';
import { Highlight, Paragraph, Text } from '../common/Typography';
import GoalDetailSectorsChartItem from './GoalDetailSectorsChartItem';

interface GoalDetailInvestmentInfoSectionProps extends FlexProps {
    goalAccount: PlentyGoalAccountFinancialEntity;
}

const GoalDetailInvestmentInfoSection = ({
    goalAccount,
    ...props
}: GoalDetailInvestmentInfoSectionProps) => {
    const { investmentInfoRowsProps, title } = useInvestmentInfoRowsProps({
        goalAccount,
    });

    return (
        <EntityViewGridSection title={title} {...props}>
            <FlexCol gap={4} mb={4}>
                {investmentInfoRowsProps.map((props) => (
                    <InvestmentInfoRow key={props.label} {...props} />
                ))}
            </FlexCol>
            <GoalDetailSectorsChartItem
                investmentType={goalAccount.info.investmentType}
                riskScore={
                    goalAccount.info.investmentType === 'HIGH_YIELD_CASH'
                        ? 0
                        : goalAccount.info.riskScore
                }
            />
        </EntityViewGridSection>
    );
};

const InvestmentInfoRow = ({
    label,
    value,
    valueIconName,
    valueHighlightColorScheme,
    infoTitle,
    infoParagraphs,
    infoFormulaItems,
}: InvestmentInfoRowProps) => {
    return (
        <FlexRow justify="space-between" align="center">
            <FlexRow align="center">
                <Text mr={0.5}>{label}</Text>
                {infoTitle && (
                    <InfoButtonWithModal
                        title={infoTitle}
                        buttonProps={{
                            onClickEventParams: {
                                location: 'Goal Detail',
                                label,
                            },
                        }}
                    >
                        {infoParagraphs?.map((str, idx) => {
                            return <Paragraph key={idx}>{str}</Paragraph>;
                        })}
                        {infoFormulaItems && (
                            <>
                                <Paragraph>The formula is:</Paragraph>
                                <FormulaRow items={infoFormulaItems} />
                            </>
                        )}
                    </InfoButtonWithModal>
                )}
            </FlexRow>
            <FlexRow alignItems="center">
                {valueIconName && (
                    <Icon name={valueIconName} size="sm" mr={2} />
                )}
                {valueHighlightColorScheme === 'green' ? (
                    <Text color="textMuted">
                        <Highlight
                            fontSize="md"
                            py={1}
                            px={3}
                            rounded={15}
                            colorScheme="green"
                            color={styleguide.colors.greenDark2X}
                            fontWeight={500}
                            letterSpacing="normal"
                        >
                            {value}
                        </Highlight>
                    </Text>
                ) : (
                    <Text color="textMuted">{value}</Text>
                )}
            </FlexRow>
        </FlexRow>
    );
};

export default GoalDetailInvestmentInfoSection;
